import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, Edit3, Star, ArrowLeft, Send, Download, CheckSquare } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

const ArticleCard = ({ article, selected, onToggle, onEdit }) => (
  <div 
    onClick={() => onToggle(article.id)}
    className={`flex flex-col md:flex-row bg-white border rounded-2xl p-4 transition-all duration-300 cursor-pointer group relative ${
      selected ? 'border-[#256df3] ring-1 ring-[#256df3]/20 shadow-md bg-blue-50/10' : 'border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'
    }`}
  >
    {/* Thumbnail */}
    <div className="w-full md:w-32 h-32 md:h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 mb-3 md:mb-0 relative">
      {/* Thumbnail or Placeholder */}
      {(article.images && article.images.length > 0) || (article.generated_images) ? (
        <img 
          src={((article.images && article.images.length > 0) ? article.images[0] : (article.generated_images ? article.generated_images.split(';')[0] : '')).startsWith('/') ? API_URL.replace('/api', '') + ((article.images && article.images.length > 0) ? article.images[0] : (article.generated_images ? article.generated_images.split(';')[0] : '')) : ((article.images && article.images.length > 0) ? article.images[0] : (article.generated_images ? article.generated_images.split(';')[0] : ''))} 
          alt={article.title} 
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/300?text=Error"; }}
        />
      ) : (
        <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400">
          <span className="text-xs font-mono">无图</span>
        </div>
      )}
      {/* Checkbox Overlay (Visible on hover or selected) */}
      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selected ? 'bg-[#256df3] border-[#256df3] opacity-100' : 'bg-white border-gray-300 opacity-0 group-hover:opacity-100'}`}>
        {selected && <CheckSquare size={14} className="text-white" />}
      </div>
      {/* Score Badge (Top Left over image) */}
       <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-[#256df3] text-white text-[10px] font-bold rounded flex items-center shadow-sm">
        <Star size={10} className="mr-0.5" fill="currentColor" />
        {article.score}
      </div>
    </div>
    
    {/* Content */}
    <div className="flex-1 md:ml-4 flex flex-col justify-between">
      <div>
        <div className="flex justify-between items-start">
          <h3 className="text-base font-bold text-gray-800 mb-1 line-clamp-1 group-hover:text-[#256df3] transition-colors">
            {article.title}
          </h3>
          <div className="flex items-center space-x-2">
            <button 
                onClick={(e) => { e.stopPropagation(); onEdit(article.id); }}
                className="p-1.5 text-gray-400 hover:text-[#256df3] hover:bg-blue-50 rounded-lg transition-colors" 
                title="编辑"
            >
              <Edit3 size={16} />
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 md:line-clamp-2">
          <span className="font-medium text-gray-600">内容摘要：</span>
          {article.content ? article.content.substring(0, 60) + '...' : '暂无内容'}
        </p>
      </div>
      
      {/* Tags / Meta */}
      <div className="flex items-center space-x-2 mt-2">
         <span className="px-2 py-0.5 bg-gray-50 text-gray-500 text-[10px] rounded border border-gray-100">
          {article.category}
         </span>
         <span className="text-[10px] text-gray-400">
          {article.wordCount} 字
         </span>
      </div>
    </div>
  </div>
);

export default function GeneratedList() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && id) fetchArticles();
  }, [id, user]);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API_URL}/tasks/${id}/articles`, {
        headers: { 'x-user-email': user?.email }
      });
      if (res.data && Array.isArray(res.data)) {
        setArticles(res.data);
      } else {
        setArticles([]);
      }
    } catch (err) {
      console.error("Failed to fetch articles", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (articleId) => {
    if (selectedIds.includes(articleId)) {
      setSelectedIds(selectedIds.filter(i => i !== articleId));
    } else {
      setSelectedIds([...selectedIds, articleId]);
    }
  };

  const selectAll = () => {
    if (selectedIds.length === articles.length) setSelectedIds([]);
    else setSelectedIds(articles.map(a => a.id));
  };

  const handleEdit = (articleId) => {
    navigate(`/tasks/${id}/editor/${articleId}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePublish = () => {
      alert(`已选择 ${selectedIds.length} 篇文章进行发布！`);
      navigate('/tasks');
  }

  const handleDownload = async () => {
    if (selectedIds.length === 0) {
      alert("请至少选择一篇文章");
      return;
    }

    setIsDownloading(true);
    const zip = new JSZip();

    try {
      const taskFolder = zip.folder(`Task_${id}_Articles`);
      const baseUrl = API_URL.replace('/api', '');

      for (const articleId of selectedIds) {
        const article = articles.find(a => a.id === articleId);
        if (article) {
          const folderName = (article.title || `Article_${article.id}`).replace(/[\\/:*?"<>|]/g, "_");
          const articleFolder = taskFolder.folder(folderName);
          const textContent = `标题：${article.title || ''}\n字数：${article.wordCount || 0}\n\n${article.content || ''}`;
          articleFolder.file("content.txt", textContent);
          
          if (article.images && Array.isArray(article.images)) {
            for (let i = 0; i < article.images.length; i++) {
              let imgPath = article.images[i];
              if (!imgPath) continue;
              if (imgPath.startsWith('/')) {
                imgPath = baseUrl + imgPath;
              }
              try {
                const response = await fetch(imgPath);
                if (response.ok) {
                  const blob = await response.blob();
                  articleFolder.file(`image_${i + 1}.jpg`, blob);
                }
              } catch (err) {
                console.error("Failed to fetch image:", imgPath, err);
              }
            }
          }
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `Task_${id}_Selected_Articles.zip`);

    } catch (error) {
      console.error("Download failed:", error);
      alert("下载失败，请重试");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Fixed Full-Width Header */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex flex-col md:flex-row md:items-center justify-between z-30 shadow-sm">
        <div>
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-1">
            <span>任务 #{id}</span>
            <span>/</span>
            <span className="text-[#256df3] font-medium">生成的文章列表 (数据库版)</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">请从以下生成结果中选择</h1>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
          <button 
            onClick={selectAll}
            className="text-sm text-gray-500 hover:text-[#256df3] font-medium transition-colors"
          >
            {articles.length > 0 && selectedIds.length === articles.length ? "取消全选" : "全选本页"}
          </button>
        </div>
      </header>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar pb-24">
        <div className="container mx-auto px-4 md:px-8 py-6 md:py-8 max-w-7xl">
          {/* List Content */}
          {loading ? (
            <div className="text-center py-20 text-gray-500">加载文章中...</div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20 text-gray-400 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                暂无生成结果，请返回任务列表确认任务是否完成。
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {articles.map(article => (
                <ArticleCard 
                    key={article.id} 
                    article={article} 
                    selected={selectedIds.includes(article.id)}
                    onToggle={toggleSelection}
                    onEdit={handleEdit}
                />
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Sticky Footer Action Bar */}
      <div className="fixed bottom-0 left-0 md:left-64 right-0 p-4 bg-white border-t border-gray-100 flex items-center justify-between z-40 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.1)]">
        <button 
            onClick={handleBack}
            className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
        >
          <ArrowLeft size={18} />
          <span>返回上一步</span>
        </button>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500 hidden sm:inline-block">已选择 <b className="text-[#256df3]">{selectedIds.length}</b> 篇文章</span>
          
          <button 
            onClick={handleDownload}
            disabled={isDownloading || selectedIds.length === 0}
            className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            <Download size={18} />
            <span>{isDownloading ? "打包中..." : "批量下载"}</span>
          </button>

          <button 
            onClick={() => alert('暂未连接发布渠道')}
            className="px-8 py-2.5 bg-gradient-to-r from-[#256df3] to-[#37a7f3] text-white rounded-xl shadow-lg shadow-blue-200 font-bold hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 flex items-center space-x-2"
          >
            <Send size={18} />
            <span>发布选中的文章</span>
          </button>
        </div>
      </div>
    </div>
  );
}